import React, { useEffect } from "react";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Artist } from "types/artist";
import { useParams } from "react-router";
import { toast } from 'react-toastify';


export interface PlatformLinks {
  social_platform_id: string;
  url: string;

}

export const useAuthors = (
  status: string = '',
  currentPage: number = 0,
  pageCount: number = 10,
  search: string = ''
) => {
  const [authors, setAuthors] = React.useState<Artist[]>([]);
  const [author, setAuthor] = React.useState({});
  const [fetching, setFetching] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const { id } = useParams()


  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);

  useEffect(() => {
    (async () => await getAuthors(status, currentPage, pageCount, search))();
  }, []);
  useEffect(() => {
    (async () => await getAuthors(
      status,
      currentPage,
      pageCount,
      search
    ))();
  }, [status, currentPage, pageCount, search]);

  const refreshAuthors = async () => {
    await getAuthors(status, currentPage, pageCount, search);
  };



  const getAuthors = async (
    status: string = '',
    currentPage: number = 0,
    pageCount: number = 10,
    search: string = ''
  ) => {
    try {
      setFetching(true);

      const queryParams = new URLSearchParams({
        status,
        page: (currentPage).toString(),
        per_page: pageCount.toString(),
        name: search
      });

      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.Authors}?${queryParams.toString()}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTotal(response.data.meta.total);
      setAuthors(response.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setFetching(false);
    }
  };

  const getAuthor = async (id: string) => {
    try {
      setFetching(true);
      const response = await axios.get(
        `${API_BASE_URL}${API_URLS.Authors}/${id}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAuthors(response.data.data);
    } catch (error) {
      console.error('Failed to fetch artist:', error);
    } finally {
      setFetching(false);

    }
  };


  const createAuthor = async (
    name: string,
  ) => {
    try {
      setFetching(true);

      if (name.length < 3 || name.length > 30) {
        throw new Error('Name must be between 3 and 30 characters');
      }

      setCreating(true);
      const result = await axios.post(
        `${API_BASE_URL}${API_URLS.Authors}`,
        {
          name,
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await refreshAuthors();

      return {
        ...result.data.data,
      };
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errorMessage =
          e.response?.data?.errors?.name?.[0]?.message || e.response?.data?.message ||
          'Something went wrong';
        toast.error(errorMessage);
      } else if (e instanceof Error) {
        toast.error(e.message || 'Something went wrong');
      }
    } finally {
      setFetching(false);

      setCreating(false);
    }
  };



  const removeAuthor = async (id: string) => {
    try {
      setFetching(true);

      setCreating(true);
      await axios.delete(
        `${API_BASE_URL}${API_URLS.Authors}/${id}`,
        { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } }
      );
      await refreshAuthors();
    } catch (e: any) {
      let errorMessage = 'Something went wrong';

      if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.errors) {
        const errors = e.response.data.errors;
        const allErrors = Object.values(errors)
          .flat()
          .map((err: any) => err.message || JSON.stringify(err))
          .join('\n');
        errorMessage = allErrors;
      } else if (e.message) {
        errorMessage = e.message;
      }

      throw new Error(errorMessage);
    } finally {
      setCreating(false);
      setFetching(false);

    }
  };


  const upDateAuthor = async (id: string, data = {}) => {
    try {
      setFetching(true);
      setCreating(true);
      await axios.put(
        `${API_BASE_URL}${API_URLS.Authors}/${id}`,
        { ...data },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await refreshAuthors();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errorMessage =
          e.response?.data?.errors?.name?.[0]?.message || e.response?.data?.message ||
          'Something went wrong';
        toast.error(errorMessage);
      }
    } finally {
      setFetching(false);
      setCreating(false);
    }
  };


  return { authors, creating, fetching, createAuthor, removeAuthor, total, upDateAuthor, getAuthors, author };
};
